// apps/share-web/src/player-view/chart-wiring.ts
//
// Чистые помощники проводки чарта: всё, что можно решить без DOM и без плеера.
// Держится отдельно от mount.ts осознанно — инвариант роста волны: «новая фича =
// новые модули + тонкая оркестрация». В mount.ts остаётся только сборка, здесь
// живут решения, и каждое из них проверяется тестом.
//
// Ни одна функция здесь не читает `document`, `window` и состояние: ширина
// приходит числом, манифест — аргументом. Это и есть причина, по которой они
// проверяемы в happy-dom, который раскладку не считает вовсе.

import type { ShareManifest } from '@bandway/share-types';
import type { SectionSec } from '../chart/chart-follow.js';
import { gridSections, songMetaLine } from '../chart/chart-song.js';

// Мета песни — правило ОБЩЕЕ с текстовой ссылкой волны B, поэтому оно живёт в
// chart/chart-song.ts, а здесь только переиздаётся: mount.ts и его тесты берут
// её отсюда с волны A, и переезд импортов ничего не улучшил бы. Реализация одна.
export { songMetaLine };

/**
 * Порог мобильной раскладки. Совпадает с `@media (max-width: 767px)` в
 * player-view.css — это не совпадение, а условие: на телефоне кнопка встаёт под
 * волну обзора (в тот самый блок, где `.pv-overview-col` получает `order: -1`) и
 * открывает сразу полный экран. Разъедутся числа — разъедутся раскладка и
 * поведение.
 */
export const PHONE_MAX = 767;

export const isPhoneWidth = (w: number): boolean => w <= PHONE_MAX;

/**
 * Текст чарта из манифеста. Отсутствие поля — НОРМА, а не ошибка: так выглядит
 * каждый шейр, опубликованный до этой волны, и так же выглядит песня без слов
 * (share-export не пишет поле, если текст пуст).
 *
 * Текст отдаётся дословно: пустые строки и отбивка — авторская вёрстка чарта, на
 * неё опирается разбор ChordPro. Обрезка здесь была бы порчей данных; судит о
 * наличии слов `hasChart`.
 */
export function chartTextOf(m: ShareManifest): string {
  const t = (m as { chart?: { text?: unknown } } | null | undefined)?.chart?.text;
  return typeof t === 'string' ? t : '';
}

/**
 * Хранимые связи «id секции → заголовок» из манифеста.
 *
 * Читается терпимо ровно как текст рядом: манифест приходит из сети, и «поля
 * нет» — законное состояние (каждый шейр, опубликованный до того, как связи
 * поехали на провод). Отсутствие означает «выводи по именам», и решает это
 * ядро, а не поверхность.
 */
export function chartLinksOf(m: ShareManifest): Record<string, string> | null {
  const l = (m as { chart?: { links?: unknown } } | null | undefined)?.chart?.links;
  if (!l || typeof l !== 'object' || Array.isArray(l)) return null;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(l as Record<string, unknown>)) {
    if (typeof k === 'string' && k && typeof v === 'string' && v.trim()) out[k] = v;
  }
  return Object.keys(out).length ? out : null;
}

/**
 * Есть ли что открывать. Гейт на кнопку и на панель — прямое требование
 * контракта манифеста: «"no chart" и "no words" — одно состояние для читателя,
 * НИКОГДА пустая поверхность» (`ShareManifest.chart`, packages/share-types).
 * Кнопка «Chart & Lyrics», которая умеет только сказать «слов пока нет», — это
 * шум на каждом старом шейре.
 */
export function hasChart(m: ShareManifest): boolean {
  return chartTextOf(m).trim() !== '';
}

/**
 * Секции для следования — ПОЛНАЯ сетка аранжировки, с безымянными тоже. В
 * манифесте они уже в секундах (`sectionsToSeconds` сделал это на десктопе при
 * экспорте), поэтому пересчёта здесь нет.
 *
 * ⚠️ Здесь стоял отбор по имени (`namedSections`), и это был дефект
 * B-260829 — тот же, что у текстовой ссылки, починенный 29.08. Замер настоящей
 * песни, прогнанной через настоящий экспортёр: расписание теряло проигрыш
 * 40.62 → 49.85 и хвост 68.31 → 73.85, то есть **14,65 с песни выпадали из
 * расписания вовсе**. Экспортёр тут ни при чём: он пишет `name: s.name || ''` и
 * по имени не фильтрует (share-manifest.js) — манифест несёт все пять секций,
 * терял их читатель.
 *
 * Что при этом видел читатель к 31.08: подсветку в дыре уже снимало (общий фикс
 * `chart-follow.ts` касался обеих поверхностей), а вот ряд «сейчас → дальше»
 * девять секунд проигрыша молчал ДВУМЯ чертами вместо «дальше: CHORUS» — ровно
 * в тот момент, когда музыкант поднимает голову и хочет знать, куда смотреть.
 *
 * Имя решает РОВНО ОДНО: найдётся ли для секции блок листа. Это решение живёт
 * там, где сопоставляют (`buildMap` в chart-follow.ts), а не в форме
 * расписания — позиция принадлежит всей аранжировке. Здесь остаётся ровно то,
 * что специфично для манифеста: где в нём лежат секции.
 */
export function chartSectionsFromManifest(m: ShareManifest): SectionSec[] {
  return gridSections(m?.sections);
}
